import type { MatchStatus } from './types';

/** Before a ball is kicked. Lineups being confirmed is still pre-match. */
export const isPreMatch = (status: MatchStatus): boolean =>
  status === 'SCHEDULED' || status === 'LINEUPS_CONFIRMED';

/**
 * The ball is in play. Half time counts: the match is underway and the wire is
 * still delivering, it is a pause rather than an end.
 */
export const isMatchPlaying = (status: MatchStatus): boolean =>
  status === 'LIVE' || status === 'SECOND_HALF' || status === 'HALF_TIME';

/**
 * The match is over as far as a player's minutes are concerned.
 *
 * `RECONCILING` is included because the whistle has gone: FPL settlement is
 * still moving bonus around, but nobody is going to come on and play.
 */
export const isPostMatch = (
  status: MatchStatus,
  isFinalised: boolean
): boolean => status === 'FULL_TIME' || status === 'RECONCILING' || isFinalised;

/**
 * A fixture that will never be played, so it will never settle either.
 *
 * These are terminal but never finalised, which makes them distinct from a
 * match that finished: no minutes, no bonus, no settlement timestamp ever
 * arrives.
 */
export const isFixtureAbandoned = (status: MatchStatus): boolean =>
  status === 'POSTPONED' || status === 'ABANDONED';

/**
 * Whether a fixture can still change a player's contribution.
 *
 * Deliberately broader than `isPostMatch`: a postponed or abandoned fixture is
 * over for scoring purposes even though it never reached full time and never
 * gets finalised. Treating it as unsettled would leave a pick whose only
 * fixture was called off sitting on `pending` forever, so it would never blank
 * and the auto-sub behind it would never fire.
 */
export const isFixtureSettled = (
  status: MatchStatus,
  isFinalised: boolean
): boolean => isPostMatch(status, isFinalised) || isFixtureAbandoned(status);
